import { useMemo, useState } from 'react'
import { Chip, Empty } from '../components/ui.jsx'
import { api } from '../lib/api.js'

const AREA_LABEL = {
  working_capital: 'Working Capital',
  procurement: 'Procurement',
  commercial_excellence: 'Commercial Excellence',
  plant_scheduling: 'Plant Scheduling',
  cost: 'Cost',
}

// Shorten the scan's own section name so it fits a chip.
const shortFirm = (f) => (f || '').replace('Trade and Market Press, and Association Data', 'Trade press')

export default function Intel({ intel, areas, firm, onFirm, onAdded, schedule, onCadence }) {
  const [area, setArea] = useState(null)
  const [adding, setAdding] = useState(null)
  const [scan, setScan] = useState(null)
  const [busy, setBusy] = useState(false)

  // Writes a scan brief for whoever runs it. The app never runs it itself.
  const rescan = async () => {
    setBusy(true)
    try {
      setScan(await api.rescanIntel({}))
    } finally {
      setBusy(false)
    }
  }
  const items = useMemo(() => {
    let list = intel.items
    if (area) list = list.filter((i) => i.areas.includes(area))
    if (firm) list = list.filter((i) => (i.firm_group || i.firm) === firm)
    return list
  }, [intel, area, firm])

  const saveSource = async () => {
    const body = { ...adding, areas: adding.areas || [] }
    await api.addSource(body)
    setAdding(null)
    onAdded?.()
  }

  // A real finding, not a design flourish: no firm in the scan has published on
  // plant scheduling, which is why that area has no market cover to lean on.
  const schedulingCoverage = intel.items.filter((i) => i.areas.includes('plant_scheduling')).length

  return (
    <>
      <div className="topbar">
        <h1>Market intel</h1>
        <span className="topbar-note">
          {intel.items.length} items · last scan {intel.scanned_at}
        </span>
        <div className="topbar-right">
          {firm ? (
            <button className="btn on" onClick={() => onFirm(null)}>
              {shortFirm(firm)} x
            </button>
          ) : null}
          {areas.map((a) => (
            <button key={a.key} className={`btn ${area === a.key ? 'on' : ''}`} onClick={() => setArea((v) => (v === a.key ? null : a.key))}>
              {a.short}
            </button>
          ))}
          <button className="btn ghost" onClick={() => setAdding({ title: '', url: '', firm: '', date: '', claim: '' })}>
            Add a source
          </button>
          <button className="btn primary" onClick={rescan} disabled={busy}>
            {scan ? 'Brief written' : 'Re-run scan'}
          </button>
        </div>
      </div>

      <div className="body split">
        <div className="col">
          {scan ? (
            <div className="notice" style={{ marginBottom: 10, display: 'block' }}>
              <div className="notice-title">Scan brief written across {scan.sources} sources</div>
              <div className="notice-body" style={{ marginTop: 3 }}>
                Estimated {scan.estimate.usd_display} if run through the API, or plan usage if you run it in Claude Code
                or Cowork. Add what comes back with Add a source.
              </div>
              <div className="mono" style={{ fontSize: 'var(--fs-badge)', marginTop: 4, color: 'var(--ai-ink)' }}>{scan.brief_path}</div>
            </div>
          ) : null}

          <div className="row source-filters" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            <span className="lbl">Sources</span>
            {intel.firms.map((f) => (
              <button
                key={f.firm}
                className={`btn ${firm === f.firm ? 'on' : ''}`}
                onClick={() => onFirm(firm === f.firm ? null : f.firm)}
              >
                {shortFirm(f.firm)} <span className="mono" style={{ marginLeft: 4, color: 'var(--tertiary)' }}>{f.count}</span>
              </button>
            ))}
            {firm ? (
              <button className="btn link" onClick={() => onFirm(null)}>clear</button>
            ) : null}
          </div>
          {adding ? (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-header">
                <span className="card-title">Add a source</span>
                <span className="card-sub">sits alongside the scan and can fire a market-theme trigger</span>
              </div>
              <div className="card-body stack" style={{ gap: 7 }}>
                {[
                  ['title', 'Title'],
                  ['firm', 'Firm or publication'],
                  ['url', 'URL'],
                  ['date', 'Date, e.g. 1 September 2026'],
                ].map(([key, label]) => (
                  <input
                    key={key}
                    className="input"
                    placeholder={label}
                    value={adding[key] || ''}
                    onChange={(e) => setAdding({ ...adding, [key]: e.target.value })}
                  />
                ))}
                <textarea
                  className="input"
                  placeholder="The claim: what it says that matters to us"
                  value={adding.claim || ''}
                  onChange={(e) => setAdding({ ...adding, claim: e.target.value })}
                />
                <div className="row" style={{ flexWrap: 'wrap' }}>
                  <span className="lbl">Areas</span>
                  {areas.map((a) => {
                    const on = (adding.areas || []).includes(a.key)
                    return (
                      <button
                        key={a.key}
                        className={`btn ${on ? 'on' : ''}`}
                        onClick={() =>
                          setAdding({
                            ...adding,
                            areas: on
                              ? (adding.areas || []).filter((k) => k !== a.key)
                              : [...(adding.areas || []), a.key],
                          })
                        }
                      >
                        {a.short}
                      </button>
                    )
                  })}
                </div>
                <div className="row">
                  <button className="btn primary" onClick={saveSource} disabled={!(adding.title || '').trim() && !(adding.url || '').trim()}>
                    Save source
                  </button>
                  <button className="btn" onClick={() => setAdding(null)}>Cancel</button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="card-header">
              <span className="card-title">What the firms published</span>
              <span className="card-sub">newest first</span>
            </div>
            <div>
              {items.length === 0 ? <Empty>Nothing in the scan touches that area.</Empty> : null}
              {items.map((item) => (
                <div key={item.id} style={{ padding: '11px 16px', borderBottom: '0.5px solid var(--line-soft)' }}>
                  <div className="row" style={{ marginBottom: 4, flexWrap: 'wrap' }}>
                    <Chip kind="plain">{shortFirm(item.firm_group || item.firm).toUpperCase()}</Chip>
                    <span className="card-sub">{item.date || 'undated'}</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                      {item.areas.map((a) => (
                        <span
                          key={a}
                          style={{ fontSize: 'var(--fs-badge)', padding: '1px 6px', border: '0.5px solid var(--line)', color: 'var(--muted)' }}
                        >
                          {AREA_LABEL[a] || a}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ fontWeight: 500 }}>
                    {item.url ? (
                      <a href={item.url} target="_blank" rel="noreferrer" style={{ color: 'var(--ink)' }}>
                        {item.title}
                      </a>
                    ) : (
                      item.title
                    )}
                  </div>
                  {item.claim ? (
                    <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ink-soft)', marginTop: 3, lineHeight: 1.5 }}>{item.claim}</div>
                  ) : null}
                  {item.usable ? (
                    <div className="card-sub" style={{ marginTop: 5 }}>Usable: {item.usable}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rail" style={{ padding: 14 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Synthesis</span></div>
            <div className="card-body stack">
              {intel.synthesis.map((s, i) => (
                <div key={i}>
                  <div className="lbl" style={{ marginBottom: 5 }}>{s.heading}</div>
                  <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ink-soft)', lineHeight: 1.55 }}>{s.body}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card" style={{ background: 'var(--ai-tint)', borderColor: 'var(--ai-border)' }}>
            <div className="card-header" style={{ borderColor: 'var(--ai-border)' }}>
              <span className="card-title" style={{ color: 'var(--ai-ink)' }}>The gap in the market</span>
            </div>
            <div className="card-body">
              <div className="notice-body">
                {schedulingCoverage === 0
                  ? `None of the ${intel.items.length} scanned articles covers plant scheduling. There is no consultant framing to borrow, so that case has to be argued from the account's own numbers.`
                  : `${schedulingCoverage} of ${intel.items.length} scanned articles touch plant scheduling.`}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Scan cadence</span>
              {schedule ? <Chip kind={schedule.due_now ? 'awaiting' : 'plain'}>{schedule.cadence.toUpperCase()}</Chip> : null}
            </div>
            <div className="card-body">
              <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ink-soft)', marginBottom: 9 }}>
                How often the consulting scan is re-run. Items are kept with their publication date, so the feed always
                reads newest first and an older scan is never overwritten.
              </div>
              <div className="row" style={{ marginBottom: 9 }}>
                {['monthly', 'quarterly', 'biannual', 'annual'].map((c) => (
                  <button key={c} className={`btn ${schedule?.cadence === c ? 'on' : ''}`} onClick={() => onCadence(c)}>
                    {c[0].toUpperCase() + c.slice(1)}
                  </button>
                ))}
              </div>
              {schedule ? (
                <div className="mono" style={{ fontSize: 'var(--fs-pill)', color: schedule.due_now ? 'var(--warn)' : 'var(--muted)' }}>
                  last scan {schedule.last_run} · next due {schedule.next_due}
                  {schedule.due_now ? ' — due now' : ''}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
